import React, { useEffect, useMemo, useState } from 'react';  
import { useNavigate } from 'react-router-dom';  
import { FiDownload, FiFileText, FiRefreshCw } from 'react-icons/fi';  
import { portalAPI, pdfAPI } from '../../services/api';  
import { usePortalAuth } from '../../context/PortalAuthContext';  
  
const PortalInvoicesPage = () => {  
  const { user } = usePortalAuth();  
  const navigate = useNavigate();  
  
  const [invoices, setInvoices] = useState([]);  
  const [loading, setLoading] = useState(true);  
  const [filter, setFilter] = useState('');  
  
  const role = user?.role_slug || user?.role || '';  
  const title = role === 'locataire' ? 'Mes factures et loyers' : 'Mes factures';  
  
  const fetchInvoices = async () => {  
    setLoading(true);  
    try {  
      const params = {};  
      if (filter) params.status = filter;  
      const { data } = await portalAPI.billing.listInvoices(params);  
      setInvoices(data || []);  
    } catch (e) {  
      setInvoices([]);  
    } finally {  
      setLoading(false);  
    }  
  };  
  
  useEffect(() => {  
    fetchInvoices();  
    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [filter]);  
  
  const summary = useMemo(() => {  
    const total = invoices.reduce((sum, item) => sum + Number(item.total || 0), 0);  
    const paid = invoices.filter((item) => item.status === 'paid').length;  
    const pending = invoices.filter((item) => ['draft', 'sent', 'pending'].includes(item.status)).length;  
    const overdue = invoices.filter((item) => item.status === 'overdue').length;  
  
    return { total, paid, pending, overdue };  
  }, [invoices]);  
  
  const downloadPDF = async (id, e) => {  
    e.stopPropagation();  
    try {  
      const { data } = await pdfAPI.downloadInvoice(id);  
      const url = window.URL.createObjectURL(new Blob([data]));  
      const a = document.createElement('a');  
      a.href = url;  
      a.download = `facture-${id}.pdf`;  
      a.click();  
      window.URL.revokeObjectURL(url);  
    } catch (err) {  
      alert('Erreur lors du téléchargement du PDF');  
    }  
  };  
  
  const STATUS_CONFIG = {
    paid:      { label: 'Payée',      cls: 'badge-success' },
    draft:     { label: 'Brouillon',  cls: 'badge-secondary' },
    sent:      { label: 'Envoyée',    cls: 'badge-info' },
    overdue:   { label: 'En retard',  cls: 'badge-danger' },
    partial:   { label: 'Partiel',    cls: 'badge-warning' },
    pending:   { label: 'En attente', cls: 'badge-warning' },
    cancelled: { label: 'Annulée',    cls: 'badge-secondary' },
  };

  const TYPE_LABELS = {
    rent:             'Loyer',
    rent_installment: 'Loyer (tranche)',
    rent_advance:     'Avance loyer',
    sale:             'Vente',
    service:          'Service',
    commission:       'Commission',
    other:            'Autre',
  };

  const safeDate = (d) => {
    if (!d) return '-';
    const val = typeof d === 'string' ? d.split('T')[0] : d;
    const parsed = new Date(val);
    if (isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const columns = [
    { header: 'N° Facture', render: (r) => r.invoice_number || r.id.slice(0, 8) },
    { header: 'Type', render: (r) => TYPE_LABELS[r.type] || r.type || '-' },
    {
      header: 'Bénéficiaire',
      render: (r) =>
        r.tenant_first_name || r.client_first_name
          ? `${r.tenant_first_name || r.client_first_name} ${r.tenant_last_name || r.client_last_name}`
          : '-',
    },
    { header: 'Émission', render: (r) => safeDate(r.issue_date) },
    { header: 'Échéance', render: (r) => safeDate(r.due_date) },
    { header: 'Montant', render: (r) => `${Number(r.total || 0).toLocaleString('fr-FR')} XOF` },
    {
      header: 'Statut',
      render: (r) => {
        const cfg = STATUS_CONFIG[r.status] || { label: r.status, cls: 'badge-secondary' };
        return <span className={`badge ${cfg.cls}`}>{cfg.label}</span>;
      },
    },
    {
      header: '',
      render: (r) => (
        <button className="btn btn-sm btn-secondary" onClick={(e) => downloadPDF(r.id, e)} title="Télécharger le PDF">
          <FiDownload />
        </button>
      ),
      width: '60px',
    },
  ];  
  
  return (  
    <div>  
      <div className="page-header">  
        <div>  
          <h1>{title}</h1>  
          <p style={{ color: 'var(--text-secondary)', marginTop: 4 }}>  
            Consultez vos factures et téléchargez les documents associés.  
          </p>  
        </div>  
  
        <button className="btn btn-secondary" onClick={fetchInvoices}>  
          <FiRefreshCw />  
          Actualiser  
        </button>  
      </div>  
  
      <div className="grid-4" style={{ marginBottom: 20 }}>  
        <div className="card">  
          <div className="card-body">  
            <div className="stat-label">Total</div>  
            <div className="stat-value">{summary.total.toLocaleString('fr-FR')} XOF</div>  
          </div>  
        </div>  
        <div className="card">  
          <div className="card-body">  
            <div className="stat-label">Payées</div>  
            <div className="stat-value">{summary.paid}</div>  
          </div>  
        </div>  
        <div className="card">  
          <div className="card-body">  
            <div className="stat-label">En attente</div>  
            <div className="stat-value">{summary.pending}</div>  
          </div>  
        </div>  
        <div className="card">  
          <div className="card-body">  
            <div className="stat-label">En retard</div>  
            <div className="stat-value">{summary.overdue}</div>  
          </div>  
        </div>  
      </div>  
  
      <div className="card" style={{ marginBottom: 16 }}>  
        <div className="card-body" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>  
          {['', 'draft', 'sent', 'paid', 'overdue'].map((s) => (  
            <button  
              key={s || 'all'}  
              className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`}  
              onClick={() => setFilter(s)}  
            >  
              {s === '' ? 'Toutes' : s === 'draft' ? 'Brouillon' : s === 'sent' ? 'Envoyées' : s === 'paid' ? 'Payées' : 'En retard'}  
            </button>  
          ))}  
        </div>  
      </div>  
  
      {loading ? (  
        <div className="loading">  
          <div className="spinner" />  
        </div>  
      ) : (  
        <div className="card">  
          <div className="card-header">  
            <h2>  
              <FiFileText /> Liste des factures  
            </h2>  
          </div>  
  
          <div className="table-responsive">  
            <table className="table">  
              <thead>  
                <tr>  
                  {columns.map((col, idx) => (  
                    <th key={idx} style={col.width ? { width: col.width } : undefined}>  
                      {col.header}  
                    </th>  
                  ))}  
                </tr>  
              </thead>  
              <tbody>  
                {invoices.length === 0 ? (  
                  <tr>  
                    <td colSpan={columns.length} style={{ textAlign: 'center', padding: 24 }}>  
                      Aucune facture trouvée.  
                    </td>  
                  </tr>  
                ) : (  
                  invoices.map((row) => (  
                    <tr  
                      key={row.id}  
                      onClick={() => navigate(`/portal/invoices/${row.id}`)}  
                      style={{ cursor: 'pointer' }}  
                    >  
                      {columns.map((col, idx) => (  
                        <td key={idx}>  
                          {col.render ? col.render(row) : row[col.accessor]}  
                        </td>  
                      ))}  
                    </tr>  
                  ))  
                )}  
              </tbody>  
            </table>  
          </div>  
        </div>  
      )}  
    </div>  
  );  
};  
  
export default PortalInvoicesPage;